import { ArrowBackOutlined, BusinessOutlined, SaveOutlined } from '@mui/icons-material';
import { Alert, Box, Button, IconButton, Stack, Tooltip } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  FeedbackSnackbar,
  FormTextField,
  EditPage,
  PageSections,
  ResourceState,
  SectionCard,
} from '../../../design-system';
import { CompanyLegalDetailCard } from '../components/CompanyLegalDetailCard';
import {
  characterLength,
  companyLegalKey,
  normalizeCompanyLegalProfile,
  validCompanyLegalProfile,
} from '../model/companyLegal';
import { normalizeOrganizationSettings } from '../model/organization';
import { errorCode } from '../model/resource';
import type { TenantSettingsPageProps } from '../model/types';
import { useCompanyWorkspace } from '../model/useCompanyWorkspace';
import { useOrganizationWorkspace } from '../model/useOrganizationWorkspace';
import { useTenantSettingsRoute } from '../model/useTenantSettingsRoute';
export function CompanyInformationPage({
  port,
  route,
  fixtureMode,
  access,
  onOrganizationChanged,
}: TenantSettingsPageProps) {
  const location = useTenantSettingsRoute('admin-company-info', route);
  const canWrite =
    access.scopes.includes('tenant.settings.read') &&
    !access.readOnly &&
    access.scopes.includes('tenant.settings.write');
  const profiles = useCompanyWorkspace(port, fixtureMode);
  const organizationWorkspace = useOrganizationWorkspace(port, fixtureMode, onOrganizationChanged);
  const { draft, baseline, setDraft, setBaseline } = profiles;
  const {
    resource: organization,
    draft: organizationDraft,
    baseline: organizationBaseline,
    setDraft: setOrganizationDraft,
    setBaseline: setOrganizationBaseline,
  } = organizationWorkspace;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const primary =
    profiles.companies.find((profile) => profile.isPrimary) ??
    (profiles.companies.length === 1 ? profiles.companies[0] : undefined);
  useEffect(() => {
    if (!primary || (draft && companyLegalKey(draft) !== companyLegalKey(baseline))) return;
    setDraft(primary);
    setBaseline(primary);
  }, [baseline, draft, primary]);
  const activeDraft = draft?.companyId === primary?.companyId ? draft : undefined;
  const activeBaseline = baseline?.companyId === primary?.companyId ? baseline : undefined;
  const companyDirty = companyLegalKey(activeDraft) !== companyLegalKey(activeBaseline);
  const companyValid = validCompanyLegalProfile(activeDraft);
  const nameDirty = organizationDraft.displayName.trim() !== organizationBaseline.displayName;
  const nameLength = characterLength(organizationDraft.displayName);
  const nameValid = nameLength >= 1 && nameLength <= 120;
  const saveCompany = () => {
    if (!activeDraft || !companyDirty || !companyValid || !canWrite || saving) return;
    setSaving(true);
    setError(undefined);
    setNotice(undefined);
    const normalizedDraft = normalizeCompanyLegalProfile({
      ...activeDraft,
      descriptor: activeDraft.descriptor.trim(),
      legalName: activeDraft.legalName.trim(),
      mainPhoneNumber: activeDraft.mainPhoneNumber.trim(),
      registeredAddress: activeDraft.registeredAddress.trim(),
      representativeTitle: activeDraft.representativeTitle.trim(),
      representativeName: activeDraft.representativeName.trim(),
    });
    const request = fixtureMode
      ? Promise.resolve({
          ...normalizedDraft,
          version: normalizedDraft.version + 1,
          updatedAt: new Date().toISOString(),
        })
      : port.updateCompanyLegalProfile(normalizedDraft.companyId, normalizedDraft);
    void request
      .then(
        (profile) => {
          const normalized = normalizeCompanyLegalProfile(profile);
          setDraft(normalized);
          setBaseline(normalized);
          profiles.replace(normalized);
          setEditing(false);
          setNotice(`${normalized.descriptor} 법인 정보를 저장했습니다.`);
        },
        (reason: unknown) => {
          if (errorCode(reason) === 'VERSION_CONFLICT') {
            setError('다른 사용자가 이 법인 정보를 먼저 변경했습니다. 최신 값을 다시 확인해 주세요.');
            setDraft(undefined);
            setBaseline(undefined);
            profiles.resource.reload();
          } else if (errorCode(reason) === 'REGISTRATION_NUMBER_CONFLICT')
            setError('입력한 등록번호가 다른 법인에 이미 등록되어 있습니다.');
          else setError(reason instanceof Error ? reason.message : '법인 정보를 저장하지 못했습니다.');
        },
      )
      .finally(() => setSaving(false));
  };
  const saveName = () => {
    if (!nameDirty || !nameValid || !canWrite || saving) return;
    setSaving(true);
    setError(undefined);
    setNotice(undefined);
    const request = fixtureMode
      ? Promise.resolve({
          ...organizationDraft,
          displayName: organizationDraft.displayName.trim(),
          version: organizationBaseline.version + 1,
          updatedAt: new Date().toISOString(),
        })
      : port.updateOrganizationSettings(organizationDraft.displayName.trim());
    void request
      .then(
        (settings) => {
          const normalized = normalizeOrganizationSettings(settings);
          setOrganizationDraft((current) => ({
            ...normalized,
            brandTheme: current.brandTheme,
          }));
          setOrganizationBaseline(normalized);
          onOrganizationChanged(normalized);
          setNotice('표시 이름을 저장했습니다.');
        },
        (reason: unknown) => setError(reason instanceof Error ? reason.message : '표시 이름을 저장하지 못했습니다.'),
      )
      .finally(() => setSaving(false));
  };
  const nameView = location.view === 'organization-name';
  const cancelCompany = () => {
    if (saving) return;
    if (activeBaseline) setDraft(activeBaseline);
    setEditing(false);
    setError(undefined);
    setNotice(undefined);
  };
  return (
    <EditPage
      title={nameView ? '표시 이름' : '회사 정보'}
      titleRef={location.titleRef}
      width="compact"
      presentation={nameView ? 'plain' : 'sectioned'}
      navigation={
        nameView ? (
          <Tooltip title="회사 정보로 돌아가기">
            <span>
              <IconButton
                aria-label="회사 정보로 돌아가기"
                onClick={() => location.navigate('company-info')}
                disabled={saving}
              >
                <ArrowBackOutlined />
              </IconButton>
            </span>
          </Tooltip>
        ) : undefined
      }
      editActions={
        canWrite && nameView ? (
          <>
            <Button
              variant="outlined"
              disabled={saving || !nameDirty}
              onClick={() => {
                if (saving) return;
                setOrganizationDraft((current) => ({ ...current, displayName: organizationBaseline.displayName }));
                setError(undefined);
                setNotice(undefined);
              }}
            >취소</Button>
            <Button
              variant="contained"
              startIcon={<SaveOutlined />}
              onClick={saveName}
              disabled={saving || !nameDirty || !nameValid}
            >
              {saving ? '저장 중…' : '변경사항 저장'}
            </Button>
          </>
        ) : undefined
      }
    >
      <Box>
        {nameView ? (
          <ResourceState
            loading={organization.loading}
            error={organization.error?.message}
            empty={false}
            onRetry={organization.reload}
          >
            <PageSections>
              {!canWrite ? <Alert severity="warning">이 설정을 조회할 수 있지만 변경 권한은 없습니다.</Alert> : null}
              {error ? <Alert severity="error">{error}</Alert> : null}
              <SectionCard
                bodyPadding="standard"
                title={
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <BusinessOutlined color="action" fontSize="small" />
                    <span>표시 이름</span>
                  </Stack>
                }
              >
                <Stack spacing={2.25}>
                  <FormTextField
                    required
                    fullWidth
                    label="표시 이름"
                    value={organizationDraft.displayName}
                    disabled={!canWrite || saving}
                    error={!nameValid}
                    helperText={
                      nameValid
                        ? undefined
                        : '표시 이름은 공백을 제외하고 1자 이상 120자 이하로 입력해 주세요.'
                    }
                    onChange={(event) => {
                      setOrganizationDraft((current) => ({
                        ...current,
                        displayName: event.target.value,
                      }));
                      setNotice(undefined);
                    }}
                    slotProps={{
                      htmlInput: {
                        maxLength: 120,
                        'aria-label': '표시 이름',
                      },
                    }}
                  />
                </Stack>
              </SectionCard>
            </PageSections>
          </ResourceState>
        ) : (
          <Box data-testid="company-legal-detail-content"><PageSections>
            {!canWrite ? (
              <Alert severity="warning">법인 공식 정보를 조회할 수 있지만 변경 권한은 없습니다.</Alert>
            ) : null}
            {error ? <Alert severity="error">{error}</Alert> : null}
            <ResourceState
              loading={profiles.resource.loading}
              error={profiles.resource.error?.message}
              empty={profiles.ready && profiles.companies.length === 0}
              onRetry={profiles.resource.reload}
              loadingLabel="회사 공식 정보를 불러오는 중입니다."
              retryLabel="회사 공식 정보 다시 시도"
              emptyTitle="등록된 회사가 없습니다."
              emptyDescription={canWrite ? '회사 목록에서 회사를 등록한 뒤 공식 정보를 설정해 주세요.' : '회사 관리자에게 회사 등록을 요청해 주세요. 등록이 끝나면 이곳에서 공식 정보를 확인할 수 있습니다.'}
              emptyAction={canWrite ? <Button variant="fill" onClick={() => route.navigate('admin-affiliates', 'companies')}>회사 목록 열기</Button> : undefined}
            >
              {activeDraft ? (
                <CompanyLegalDetailCard
                  canWrite={canWrite}
                  profile={activeDraft}
                  dirty={companyDirty}
                  valid={companyValid}
                  editing={editing}
                  saving={saving}
                  editAriaLabel="회사 기본 정보 수정"
                  onCancel={cancelCompany}
                  onEdit={() => {
                    setEditing(true);
                    setError(undefined);
                    setNotice(undefined);
                  }}
                  onSave={saveCompany}
                  onChange={(field, value) => {
                    setDraft((current) =>
                      current && current.companyId === primary?.companyId
                        ? {
                            ...current,
                            [field]: value,
                          }
                        : current,
                    );
                    setNotice(undefined);
                  }}
                />
              ) : (
                <Alert severity="warning">
                  {primary && profiles.dirty && draft
                    ? `${draft.descriptor}의 저장하지 않은 변경을 먼저 저장하거나 되돌려 주세요.`
                    : '기준 회사 정보를 찾을 수 없습니다.'}
                </Alert>
              )}
            </ResourceState>
          </PageSections></Box>
        )}
      </Box>
      <FeedbackSnackbar open={Boolean(notice)} message={notice} tone="success" onClose={() => setNotice(undefined)} />
    </EditPage>
  );
}
